import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/race-tracker/components/ui/sheet';
import { Button } from '@/race-tracker/components/ui/button';
import { Badge } from '@/race-tracker/components/ui/badge';
import { Pencil, Trash2, Phone, Mail, MapPin, Building2, User, Radio } from 'lucide-react';

function ContactLink({ type, value }) {
  if (!value) return <span className="text-muted-foreground text-sm">—</span>;
  if (type === 'email') return <a href={`mailto:${value}`} className="text-sm text-blue-600 hover:underline">{value}</a>;
  if (type === 'phone') return <a href={`tel:${value}`} className="text-sm text-blue-600 hover:underline">{value}</a>;
  return <span className="text-sm">{value}</span>;
}

function Field({ label, value, type }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {type ? <ContactLink type={type} value={value} /> : (
        <p className="text-sm text-foreground mt-0.5">{value || <span className="text-muted-foreground">—</span>}</p>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 border-b pb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {children}
      </div>
    </div>
  );
}

export default function StationDetailDrawer({ station, managers, open, onClose, onEdit, onDelete, canWrite = true }) {
  if (!station) return null;

  const firmManagers = managers?.filter(m => m.rep_firm === station.rep_firm) || [];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-xl font-bold">{station.station}</SheetTitle>
              <p className="text-sm text-muted-foreground mt-1">{station.market}</p>
            </div>
            {canWrite && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(station)}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(station)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-2">
            {station.affiliate && <Badge variant="secondary">{station.affiliate}</Badge>}
            {station.ownership && <Badge variant="outline">{station.ownership}</Badge>}
          </div>
        </SheetHeader>

        <div className="space-y-6 pb-6">
          <Section title="Station" icon={Radio}>
            <Field label="State" value={station.state} />
            <Field label="Market (DMA)" value={station.market} />
            <Field label="Station" value={station.station} />
            <Field label="Affiliate" value={station.affiliate} />
            <Field label="Ownership" value={station.ownership} />
          </Section>

          <Section title="Rep Firm" icon={Building2}>
            <Field label="Rep Firm" value={station.rep_firm} />
            <Field label="Rep" value={station.rep} />
            <Field label="Rep Phone" value={station.rep_phone} type="phone" />
            <Field label="Rep Email" value={station.rep_email} type="email" />
          </Section>

          {/* Joined Manager Info */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rep Firm Manager(s)</h4>
            {firmManagers.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No rep firm manager on file.</p>
            ) : (
              firmManagers.map((mgr, i) => (
                <div key={mgr.id || i} className="text-sm space-y-1">
                  <p className="font-medium">{mgr.manager} {mgr.title && <span className="text-muted-foreground">· {mgr.title}</span>}</p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {mgr.email && <a href={`mailto:${mgr.email}`} className="text-blue-600 hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{mgr.email}</a>}
                    {mgr.office_phone && <a href={`tel:${mgr.office_phone}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{mgr.office_phone}</a>}
                    {mgr.contact_mobile && <a href={`tel:${mgr.contact_mobile}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{mgr.contact_mobile}</a>}
                  </div>
                </div>
              ))
            )}
          </div>

          <Section title="C&D Contact" icon={User}>
            <Field label="Name" value={station.cd_contact_name} />
            <Field label="Title" value={station.cd_contact_title} />
            <Field label="Phone" value={station.cd_contact_phone} type="phone" />
            <Field label="Email" value={station.cd_contact_email} type="email" />
          </Section>

          <Section title="Station Address" icon={MapPin}>
            <div className="col-span-2">
              <Field label="Address" value={station.station_address} />
            </div>
            <Field label="City" value={station.station_city} />
            <div className="flex gap-4">
              <Field label="State" value={station.station_state} />
              <Field label="ZIP" value={station.station_zip} />
            </div>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}